import React from 'react';
import PropTypes from 'prop-types';
import {connect, batch, useDispatch} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Col, Row} from 'react-bootstrap';

import {restoreTipCountForSingleCard} from 'stores/business/protocolManagementBusiness';
import SerialDilutionHeader from './SerialDilutionHeader';
import MessageBox, {getMessageBoxActionButtons} from '../../../../components/MessageBox';

import {CardType, Feedback, ProtocolEditorViewMode, SerialDilutionStep, TrayType} from '../../../../constants/dataEnum';

import withIntl from '../../../../lib/withIntl';

import {hideLoading, showLoading, showSnackbar} from '../../../../stores/actions/appStateAction';
import {showModal, hideModal} from '../../../../stores/actions/modalStateAction';
import {setCardSdProperty} from '../../../../stores/actions/cardSerialDilutionStateAction';
import {deleteCard} from '../../../../stores/business/taskListBusinessAction';
import {
  generateSdCompleteTasks,
  discardSdEdit,
  removeSdRawTasks,
  getSdRawTaskIds,
  isSdStepDefined,
  clearSerialDilutionTemporaryState,
} from '../../../../stores/business/taskListSerialDilutionBusiness';
import {globalAddUndoItem} from '../../../../stores/business/undoRedoBusinessAction';
import {
  setSerialDilutionEditStep,
  setSelectedMenu,
  setSerialDilutionStep,
  setSerialDilutionPrevStep,
  setSerialDilutionAllSample,
} from '../../../../stores/actions/protocolEditorAction';
import {initiateGlobalWorker} from '../../../../workers/initiateWorker';

function mapStateToProps(state) {
  const {serialDilutionStep, serialDilutionEditStep, serialDilutionPrevStep} = state.protocolEditorState;
  return {
    cardStateCards: state.cardState.cards,
    maxAspirateVolume: state.configState.maxAspirateVolume.value,
    maxAspirateHighVolume: state.configState.maxAspirateHighVolume.value,
    protocolSerialDilutionStep: serialDilutionStep,
    protocolEditorStateSerialDilutionEditStep: serialDilutionEditStep,
    protocolSerialDilutionPrevStep: serialDilutionPrevStep,
    protocolTrayType: state.protocolState.lastOpenedProtocol?.trayType,
    minAspirateHighVolume: state.configState.minAspirateHighVolume.value,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    taskListBusinessDeleteCard: bindActionCreators(deleteCard, dispatch),
    taskListSerialDilutionBusinessGenerateSdCompleteTasks: bindActionCreators(generateSdCompleteTasks, dispatch),
    taskListSerialDilutionBusinessDiscardSdEdit: bindActionCreators(discardSdEdit, dispatch),
    taskListSerialDilutionBusinessRemoveSdRawTasks: bindActionCreators(removeSdRawTasks, dispatch),
    taskListSerialDilutionBusinessGetSdRawTaskIds: bindActionCreators(getSdRawTaskIds, dispatch),
    taskListSerialDilutionBusinessIsSdStepDefined: bindActionCreators(isSdStepDefined, dispatch),
    modalActionShowModal: bindActionCreators(showModal, dispatch),
    modalActionHideModal: bindActionCreators(hideModal, dispatch),
    appActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
    appActionShowLoading: bindActionCreators(showLoading, dispatch),
    appActionHideLoading: bindActionCreators(hideLoading, dispatch),
    cardSerialDilutionActionSetCardSdProperty: bindActionCreators(setCardSdProperty, dispatch),
    undoRedoBusinessActionGlobalAddUndoItem: bindActionCreators(globalAddUndoItem, dispatch),
    protocolEditorStateActionSetSerialDilutionStep: bindActionCreators(setSerialDilutionStep, dispatch),
    protocolEditorStateActionSetSerialDilutionEditStep: bindActionCreators(setSerialDilutionEditStep, dispatch),
    protocolEditorStateActionSetSerialDilutionPrevStep: bindActionCreators(setSerialDilutionPrevStep, dispatch),
    protocolEditorStateSetSerialDilutionAllSample: bindActionCreators(setSerialDilutionAllSample, dispatch),
    setProtocolEditorMode: bindActionCreators(setSelectedMenu, dispatch),
  };
}

function SerialDilutionHeaderContainer({
  intl,
  selectedCard,
  cardStateCards,
  maxAspirateVolume,
  maxAspirateHighVolume,
  taskListBusinessDeleteCard,
  taskListSerialDilutionBusinessGenerateSdCompleteTasks,
  taskListSerialDilutionBusinessDiscardSdEdit,
  taskListSerialDilutionBusinessRemoveSdRawTasks,
  taskListSerialDilutionBusinessGetSdRawTaskIds,
  taskListSerialDilutionBusinessIsSdStepDefined,
  modalActionShowModal,
  modalActionHideModal,
  appActionShowSnackbar,
  appActionShowLoading,
  appActionHideLoading,
  cardSerialDilutionActionSetCardSdProperty,
  undoRedoBusinessActionGlobalAddUndoItem,
  protocolSerialDilutionStep,
  protocolEditorStateActionSetSerialDilutionStep,
  protocolEditorStateSerialDilutionEditStep,
  protocolEditorStateActionSetSerialDilutionEditStep,
  protocolSerialDilutionPrevStep,
  protocolEditorStateActionSetSerialDilutionPrevStep,
  protocolEditorStateSetSerialDilutionAllSample,
  setProtocolEditorMode,
  protocolTrayType,
  minAspirateHighVolume,
}) {
  const dispatch = useDispatch();

  const protocolMaxAspirate =
    protocolTrayType === TrayType.HIGH_VOLUME_CADDY ? maxAspirateHighVolume : maxAspirateVolume;

  const isDisallowNext = () => {
    const [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetIds, taskDiluentTargetIds] =
      taskListSerialDilutionBusinessGetSdRawTaskIds(selectedCard.ID);

    const {step, sampleVolume, aliquotVolume} = selectedCard?.sdProperty || {};
    const stepProperties = selectedCard?.sdProperty?.stepProperties ?? [];

    const protocolMinAspirate = protocolTrayType === TrayType.HIGH_VOLUME_CADDY ? minAspirateHighVolume : 0;
    const stepDiluentVolumeList = stepProperties.slice(0, step).map((prop) => prop?.stepDiluentVolume ?? 0);
    const stepSampleVolumeList = stepProperties.slice(0, step).map((prop) => prop?.stepSampleVolume ?? 0);

    switch (protocolSerialDilutionStep) {
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        return !taskDiluentSourceId;
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        return !taskSampleSourceId;
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        return !taskSampleTargetIds?.length;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        return !taskDiluentTargetIds?.length && !protocolEditorStateSerialDilutionEditStep;
      case SerialDilutionStep.SERIAL_DILUTION_SETTINGS: {
        const isDiluentComplete =
          taskDiluentSourceId &&
          stepDiluentVolumeList.length === step &&
          !stepDiluentVolumeList.includes(0) &&
          !stepDiluentVolumeList.find((e) => e > 9999);
        const isSampleComplete = taskSampleSourceId && sampleVolume && sampleVolume <= 9999;
        const isAliquotComplete = taskSampleTargetIds?.length && aliquotVolume;
        const isSerialDilutionComplete =
          taskDiluentTargetIds?.length &&
          !stepSampleVolumeList.includes(0) &&
          !stepSampleVolumeList.find((e) => e > 9999) &&
          stepSampleVolumeList.length === step;

        return !(
          isSerialDilutionComplete &&
          isAliquotComplete &&
          (!taskDiluentSourceId || isDiluentComplete) &&
          (!taskSampleSourceId || isSampleComplete) &&
          sampleVolume >= protocolMinAspirate
        );
      }
      default:
        return false;
    }
  };

  const onFinish = async (equalDiluentVolume = true, callback = () => {}) => {
    appActionShowLoading();

    initiateGlobalWorker(async () => {
      const cardSdProperty = selectedCard?.sdProperty;
      await cardSerialDilutionActionSetCardSdProperty(selectedCard?.ID, {...cardSdProperty, equalDiluentVolume});
      await taskListSerialDilutionBusinessGenerateSdCompleteTasks(selectedCard?.ID);

      dispatch(clearSerialDilutionTemporaryState());
      await protocolEditorStateActionSetSerialDilutionStep(0);
      await protocolEditorStateActionSetSerialDilutionEditStep(0);
      await setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
      undoRedoBusinessActionGlobalAddUndoItem();
      dispatch(restoreTipCountForSingleCard(selectedCard?.ID));

      appActionHideLoading();
      callback();
    });
  };

  const showDeadVolumeModal = (callback = () => {}) => {
    modalActionShowModal(
      intl.formatMessage({id: 'protocolEditor.serialDilution.deadVolume.modal.title'}),
      intl.formatMessage({id: 'protocolEditor.serialDilution.deadVolume.modal.text'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: async () => {
            modalActionHideModal();
            callback();
          },
          text: intl.formatMessage({id: 'protocolEditor.serialDilution.deadVolume.modal.firstButton'}),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: async () => {
            modalActionHideModal();
          },
          text: intl.formatMessage({id: 'protocolEditor.serialDilution.deadVolume.modal.secondButton'}),
        },
      ],
      () => {
        modalActionHideModal();
      },
      '',
      true,
    );
  };

  const showSampleVolumeMoreThanMaximumTipCapacityModal = (callback = () => {}) => {
    let equalDiluentVolume = true;
    const content = (
      <div className="bootstrap-iso">
        <Row style={{paddingBottom: 10}}>
          <Col xs={12} md={12}>
            {intl.formatMessage(
              {id: 'protocolEditor.serialDilution.sampleVolumeMoreThanMaximumTipCapacity.modal.text'},
              {maxAspirateVolume: protocolMaxAspirate},
            )}
          </Col>
        </Row>
        <Row style={{paddingBottom: 4}}>
          <Col xs={1} md={1}>
            <input
              className="option"
              defaultChecked
              type="radio"
              onChange={() => {
                equalDiluentVolume = true;
              }}
              name="sampleVolumeMoreThanMaximumTipCapacity"
            />
          </Col>
          <Col xs={11} md={11}>
            {intl.formatMessage({
              id: 'protocolEditor.serialDilution.sampleVolumeMoreThanMaximumTipCapacity.modal.option1',
            })}
          </Col>
        </Row>
        <Row>
          <Col xs={1} md={1}>
            <input
              className="option"
              type="radio"
              onChange={() => {
                equalDiluentVolume = false;
              }}
              name="sampleVolumeMoreThanMaximumTipCapacity"
            />
          </Col>
          <Col xs={11} md={11}>
            {intl.formatMessage({
              id: 'protocolEditor.serialDilution.sampleVolumeMoreThanMaximumTipCapacity.modal.option2',
            })}
          </Col>
        </Row>
      </div>
    );

    modalActionShowModal(
      intl.formatMessage({id: 'protocolEditor.serialDilution.sampleVolumeMoreThanMaximumTipCapacity.modal.title'}),
      content,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: async () => {
            modalActionHideModal();
            onFinish(equalDiluentVolume, callback);
          },
          text: intl.formatMessage({
            id: 'protocolEditor.serialDilution.sampleVolumeMoreThanMaximumTipCapacity.modal.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: async () => {
            modalActionHideModal();
          },
          text: intl.formatMessage({
            id: 'protocolEditor.serialDilution.sampleVolumeMoreThanMaximumTipCapacity.modal.secondButton',
          }),
        },
      ],
      () => {
        modalActionHideModal();
      },
      '',
      true,
    );
  };

  const handleFinish = (callback) => {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const {sampleVolume, aliquotVolume, stepProperties, step} = selectedCard?.sdProperty;
    const stepSampleVolumes = stepProperties.map((prop) => prop.stepSampleVolume);
    const lastStepSampleVolume = stepSampleVolumes[step - 1];
    const isSampleSourceDefined = taskListSerialDilutionBusinessIsSdStepDefined(
      selectedCard?.ID,
      SerialDilutionStep.SET_SAMPLE_SOURCE,
    );

    const finishCallback = () => {
      if (lastStepSampleVolume > protocolMaxAspirate) {
        showSampleVolumeMoreThanMaximumTipCapacityModal(callback);
      } else {
        onFinish(true, callback);
      }
    };

    if (aliquotVolume >= sampleVolume && isSampleSourceDefined) {
      showDeadVolumeModal(finishCallback);
    } else {
      finishCallback();
    }
  };

  // movement = -1 (back) / 0 (done) / 1 (next)
  const showEditConfirmationModal = (movement) => {
    let title;
    let message;

    switch (protocolSerialDilutionStep) {
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        title = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeSampleSource.title',
        });
        message = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeSampleSource.content',
        });
        break;
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        title = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeDiluentSource.title',
        });
        message = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeDiluentSource.content',
        });
        break;
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        title = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeSampleTarget.title',
        });
        message = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeSampleTarget.content',
        });
        break;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        title = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeDiluentTarget.title',
        });
        message = intl.formatMessage({
          id: 'protocolEditor.serialDilution.editConfirmation.changeDiluentTarget.content',
        });
        break;
      case SerialDilutionStep.SERIAL_DILUTION_SETTINGS:
        handleFinish(() => {
          batch(() => {
            appActionShowSnackbar(
              Feedback.SUCCESS,
              intl.formatMessage({id: 'protocolEditor.taskList.task.editSuccess'}),
            );
          });
        });

        return;
      default:
        break;
    }

    modalActionShowModal(
      title,
      message,
      getMessageBoxActionButtons([
        {
          type: MessageBox.ButtonType.POSITIVE,
          text: 'YES',
          clickHandler: () => {
            if (movement !== 0) {
              if (movement === -1) {
                taskListSerialDilutionBusinessRemoveSdRawTasks(selectedCard.ID, [protocolSerialDilutionStep]);
              }

              protocolEditorStateActionSetSerialDilutionStep(protocolSerialDilutionStep + movement);
              modalActionHideModal();

              return;
            }

            onFinish();

            batch(() => {
              modalActionHideModal();
              appActionShowSnackbar(
                Feedback.SUCCESS,
                intl.formatMessage({id: 'protocolEditor.taskList.task.editSuccess'}),
              );
            });
          },
        },
        {
          type: MessageBox.ButtonType.NEGATIVE,
          text: 'NO',
          clickHandler: () => {
            modalActionHideModal();
          },
        },
      ]),
      () => modalActionHideModal(),
      '',
      true,
    );
  };

  const showCancelConfirmationModal = () => {
    const title = intl.formatMessage({
      id: 'protocolEditor.serialDilution.cancelConfirmation.title',
    });
    const message = intl.formatMessage({
      id: 'protocolEditor.serialDilution.cancelConfirmation.content',
    });

    modalActionShowModal(
      title,
      message,
      getMessageBoxActionButtons([
        {
          type: MessageBox.ButtonType.POSITIVE,
          text: 'YES',
          clickHandler: async () => {
            const cardIDs = Object.keys(cardStateCards);
            const lastCard = cardStateCards[cardIDs[cardIDs.length - 1]];
            if (lastCard?.type === CardType.SERIAL_DILUTION) {
              taskListBusinessDeleteCard(selectedCard.ID, true);
            }

            await setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
            await protocolEditorStateActionSetSerialDilutionStep(0);
            await protocolEditorStateActionSetSerialDilutionPrevStep(null);
            undoRedoBusinessActionGlobalAddUndoItem();

            batch(() => {
              protocolEditorStateSetSerialDilutionAllSample(false);
              modalActionHideModal();
              appActionShowSnackbar(
                Feedback.SUCCESS,
                intl.formatMessage({id: 'protocolEditor.serialDilution.cancel.success'}),
              );
            });
          },
        },
        {
          type: MessageBox.ButtonType.NEGATIVE,
          text: 'NO',
          clickHandler: () => {
            modalActionHideModal();
          },
        },
      ]),
      () => modalActionHideModal(),
      '',
      true,
    );
  };

  const onCancelEdit = async () => {
    appActionShowLoading();

    initiateGlobalWorker(async () => {
      const isEditFromSdSettings =
        protocolSerialDilutionStep !== SerialDilutionStep.SERIAL_DILUTION_SETTINGS &&
        protocolEditorStateSerialDilutionEditStep === SerialDilutionStep.SERIAL_DILUTION_SETTINGS;

      if (!isEditFromSdSettings) await protocolEditorStateActionSetSerialDilutionEditStep(0);
      await taskListSerialDilutionBusinessDiscardSdEdit(protocolSerialDilutionPrevStep);

      if (protocolSerialDilutionPrevStep) {
        await protocolEditorStateActionSetSerialDilutionStep(protocolSerialDilutionPrevStep);
        await protocolEditorStateActionSetSerialDilutionPrevStep(null);
      } else {
        await setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
        await protocolEditorStateActionSetSerialDilutionStep(0);
      }

      undoRedoBusinessActionGlobalAddUndoItem();
      appActionHideLoading();
    });
  };

  const handleContinue = async () => {
    await protocolEditorStateActionSetSerialDilutionPrevStep(null);
    await protocolEditorStateActionSetSerialDilutionStep(SerialDilutionStep.SERIAL_DILUTION_SETTINGS);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const handleBack = async () => {
    if (
      protocolEditorStateSerialDilutionEditStep &&
      taskListSerialDilutionBusinessIsSdStepDefined(selectedCard?.ID, protocolSerialDilutionStep)
    ) {
      onCancelEdit();
      return;
    }

    const sdStepToRemove = protocolSerialDilutionStep;

    await taskListSerialDilutionBusinessRemoveSdRawTasks(selectedCard.ID, [sdStepToRemove]);
    await protocolEditorStateActionSetSerialDilutionPrevStep(null);
    await protocolEditorStateActionSetSerialDilutionStep(SerialDilutionStep.SERIAL_DILUTION_SETTINGS);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const handleCancel = async () => {
    const [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetId, taskDiluentTargetIds] =
      taskListSerialDilutionBusinessGetSdRawTaskIds(selectedCard.ID);

    if (!taskDiluentSourceId && !taskSampleSourceId && !taskSampleTargetId && !taskDiluentTargetIds?.length) {
      await taskListBusinessDeleteCard(selectedCard.ID, true);
      await setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
      undoRedoBusinessActionGlobalAddUndoItem();

      return;
    }

    if (protocolEditorStateSerialDilutionEditStep) {
      onCancelEdit();
      return;
    }

    showCancelConfirmationModal();
  };

  const handleApplyEdit = async () => {
    if (protocolSerialDilutionPrevStep) {
      if (
        !(
          protocolSerialDilutionStep !== SerialDilutionStep.SERIAL_DILUTION_SETTINGS &&
          protocolEditorStateSerialDilutionEditStep === SerialDilutionStep.SERIAL_DILUTION_SETTINGS
        )
      ) {
        await protocolEditorStateActionSetSerialDilutionEditStep(0);
      }
      await protocolEditorStateActionSetSerialDilutionStep(SerialDilutionStep.SERIAL_DILUTION_SETTINGS);
      await protocolEditorStateActionSetSerialDilutionPrevStep(null);
      undoRedoBusinessActionGlobalAddUndoItem();

      return;
    }
    showEditConfirmationModal(0);
  };

  return (
    <SerialDilutionHeader
      step={protocolSerialDilutionStep}
      onBack={handleBack}
      onNext={handleContinue}
      onApplyEdit={handleApplyEdit}
      onCancel={handleCancel}
      onFinish={handleFinish}
      disallowNext={isDisallowNext()}
      disallowFinish={isDisallowNext()}
      disallowApplayEdit={isDisallowNext()}
      hideCancelButton={
        protocolSerialDilutionStep !== SerialDilutionStep.SERIAL_DILUTION_SETTINGS &&
        !protocolEditorStateSerialDilutionEditStep
      }
      isEditing={Boolean(protocolEditorStateSerialDilutionEditStep)}
    />
  );
}

SerialDilutionHeaderContainer.propTypes = {
  intl: PropTypes.any.isRequired,
  selectedCard: PropTypes.object,
  cardStateCards: PropTypes.object,
  maxAspirateVolume: PropTypes.number,
  maxAspirateHighVolume: PropTypes.number.isRequired,
  taskListBusinessDeleteCard: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessGenerateSdCompleteTasks: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessDiscardSdEdit: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessRemoveSdRawTasks: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessGetSdRawTaskIds: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessIsSdStepDefined: PropTypes.func.isRequired,
  modalActionShowModal: PropTypes.func.isRequired,
  modalActionHideModal: PropTypes.func.isRequired,
  appActionShowSnackbar: PropTypes.func.isRequired,
  appActionShowLoading: PropTypes.func.isRequired,
  appActionHideLoading: PropTypes.func.isRequired,
  cardSerialDilutionActionSetCardSdProperty: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalAddUndoItem: PropTypes.func.isRequired,
  protocolSerialDilutionStep: PropTypes.any,
  protocolEditorStateActionSetSerialDilutionStep: PropTypes.func.isRequired,
  protocolEditorStateSerialDilutionEditStep: PropTypes.number.isRequired,
  protocolEditorStateActionSetSerialDilutionEditStep: PropTypes.func.isRequired,
  protocolSerialDilutionPrevStep: PropTypes.any,
  protocolEditorStateActionSetSerialDilutionPrevStep: PropTypes.func.isRequired,
  protocolEditorStateSetSerialDilutionAllSample: PropTypes.func.isRequired,
  setProtocolEditorMode: PropTypes.func.isRequired,
  protocolTrayType: PropTypes.string.isRequired,
  minAspirateHighVolume: PropTypes.number,
};

SerialDilutionHeaderContainer.defaultProps = {
  selectedCard: {},
  cardStateCards: {},
  maxAspirateVolume: 0,
  protocolSerialDilutionPrevStep: null,
  protocolSerialDilutionStep: null,
  minAspirateHighVolume: 0,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(SerialDilutionHeaderContainer));
